import classnames from 'classnames';
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import { SIGNUP_FORM_TYPES, USER_TYPES } from 'dpl/shared/constants/shared';
import { get } from 'dpl/shared/utils/object';
import postLoginRedirect from 'dpl/util/postLoginRedirect';
import MiniOverlayModal from 'dpl/common/mini_overlay_modal/components/MiniOverlayModal';
import { INTERNATIONAL_BREEDER_SIGNUP } from 'dpl/constants/query_params';
import { addToQuery } from 'dpl/util/queryString';
import postSignupRedirect from 'dpl/util/postSignupRedirect';
import ForgotPasswordFormV2 from 'dpl/auth/components/ForgotPasswordFormV2';
import { isMobileUA } from 'dpl/shared/utils';
import FixedTooltipPortal from 'dpl/common/tooltip/components/FixedTooltipPortal';
import { PORTAL_MOUNT_EL } from 'dpl/common/tooltip/components/FloatingTooltipWrapper';
import useCurrentUser from 'dpl/common/hooks/useCurrentUser';

import UserAuthModalLoginFormV2 from '../UserAuthModalLoginFormV2';
import Logo from '../../../components/Logo';
import SignupFormIntro from '../SignUpFormIntro';
import SignupFormV2 from '../SignupFormV2';
import UserAuthModalBreederSignupFormV2 from '../UserAuthModalBreederSignupFormV2';
import UserAuthResetPasswordFormV2 from '../UserAuthResetPasswordFormV2';

const {
  SIGNUP_INTRO,
  SIGNUP,
  BREEDER_SIGNUP,
  FORGOT_PASSWORD,
  RESET_PASSWORD
} = SIGNUP_FORM_TYPES;

const IS_MOBILE_UA = isMobileUA();

function UserAuthContent({
  clearParams,
  onBreederSignupClick,
  onLoginClick,
  onPasswordResetClick,
  onSignupClick,
  queryParams = {},
  loginOnly = false
}) {
  const { direct_signup: directSignup } = queryParams;

  return (
    <>
      {![SIGNUP_INTRO, BREEDER_SIGNUP].includes(directSignup) &&
        !IS_MOBILE_UA && (
          <div className="flex justify-center pb10">
            <div className="Header__logo primary">
              <Logo />
            </div>
          </div>
        )}
      {(() => {
        if (directSignup === SIGNUP_INTRO) {
          return <SignupFormIntro />;
        }
        if (directSignup === SIGNUP) {
          return (
            <SignupFormV2
              userType={USER_TYPES.BUYER}
              onAuth={() => {
                clearParams();
                return window.location.reload();
              }}
              onBreederSignupClick={onBreederSignupClick}
              onLoginClick={onLoginClick}
            />
          );
        }
        if (directSignup === BREEDER_SIGNUP) {
          return (
            <UserAuthModalBreederSignupFormV2
              onAuth={json => {
                const isInternationalBreeder = get(
                  json,
                  'data.user.international_breeder'
                );
                clearParams();

                if (isInternationalBreeder) {
                  window.location = `?${addToQuery(
                    { [INTERNATIONAL_BREEDER_SIGNUP]: true },
                    window.location.search
                  )}`;
                  return;
                }

                postSignupRedirect(USER_TYPES.BREEDER);
              }}
              onLoginClick={onLoginClick}
            />
          );
        }
        if (directSignup === FORGOT_PASSWORD) {
          return <ForgotPasswordFormV2 isCentered />;
        }
        if (directSignup === RESET_PASSWORD) {
          return <UserAuthResetPasswordFormV2 />;
        }
        return (
          <UserAuthModalLoginFormV2
            loginOnly={loginOnly}
            onAuth={json => {
              if (get(json, 'data.data.buyer')) {
                clearParams();
                return window.location.reload();
              }

              return postLoginRedirect(USER_TYPES.BREEDER);
            }}
            onPasswordResetClick={onPasswordResetClick}
            onSignupClick={onSignupClick}
            onBreederSignupClick={onBreederSignupClick}
          />
        );
      })()}
    </>
  );
}

UserAuthContent.propTypes = {
  clearParams: PropTypes.func.isRequired,
  onBreederSignupClick: PropTypes.func.isRequired,
  onPasswordResetClick: PropTypes.func.isRequired,
  onSignupClick: PropTypes.func.isRequired,
  onLoginClick: PropTypes.func.isRequired,
  queryParams: PropTypes.shape({
    direct_signup: PropTypes.string
  }),
  loginOnly: PropTypes.bool
};

UserAuthContent.defaultProps = {
  queryParams: {},
  loginOnly: false
};

export default function UserAuthModalV2({
  clearParams,
  queryParams,
  ...props
}) {
  const { direct_signup: directSignup } = queryParams;
  const isVisible = typeof directSignup === 'string' && IS_MOBILE_UA;
  const currentUser = useCurrentUser();
  useEffect(() => {
    isVisible
      ? PORTAL_MOUNT_EL.classList.add('no-scroll')
      : PORTAL_MOUNT_EL.classList.remove('no-scroll');
  }, [isVisible, IS_MOBILE_UA]);
  if (typeof directSignup !== 'string') {
    return null;
  }

  const { isLoggedIn } = currentUser;
  if (isLoggedIn) {
    return null;
  }

  const specialStyles = ![SIGNUP_INTRO, BREEDER_SIGNUP].includes(directSignup);

  return (
    <>
      {!IS_MOBILE_UA ? (
        <MiniOverlayModal
          closeButtonClassName="padding-h-6 padding-v-6 mt4 mr4 br-100 bg-white b--gray-300 ba"
          className="UserAuthModal"
          contentClassName={classnames('bg-sand', {
            'pt6 pb12 ph16': specialStyles
          })}
          onCloseClick={clearParams}
        >
          <UserAuthContent
            queryParams={queryParams}
            clearParams={clearParams}
            {...props}
          />
        </MiniOverlayModal>
      ) : (
        <FixedTooltipPortal
          isVisible
          className={classnames('bg-sand pt8 ph5', {
            'vh-80': directSignup !== SIGNUP_INTRO,
            'vh-50': directSignup === SIGNUP_INTRO
          })}
          contentClassName={classnames('bg-sand', {
            'pt6 pb12 pl16 pr16': specialStyles
          })}
          onCloseClick={clearParams}
        >
          <UserAuthContent
            queryParams={queryParams}
            clearParams={clearParams}
            {...props}
          />
        </FixedTooltipPortal>
      )}
    </>
  );
}

UserAuthModalV2.propTypes = {
  clearParams: PropTypes.func.isRequired,
  onBreederSignupClick: PropTypes.func.isRequired,
  onPasswordResetClick: PropTypes.func.isRequired,
  onSignupClick: PropTypes.func.isRequired,
  onLoginClick: PropTypes.func.isRequired,
  queryParams: PropTypes.shape({
    direct_signup: PropTypes.string
  }),
  loginOnly: PropTypes.bool
};

UserAuthModalV2.defaultProps = {
  queryParams: {},
  loginOnly: false
};
